import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import '../styles/searchable-select.css';

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  label,
  required = false,
  disabled = false,
  className = '',
  triggerClassName = '',
  optionLabel = 'title',
  optionValue = 'id',
  labelLines = 1,
  multiple = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = triggerRef.current?.contains(event.target);
      const inDropdown = dropdownRef.current?.contains(event.target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setMenuStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportPadding = 8;
      const maxMenuHeight = labelLines > 1 ? 320 : 260;
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
      const availableHeight = openUpward ? spaceAbove - 4 : spaceBelow - 4;
      const menuHeight = Math.min(maxMenuHeight, Math.max(availableHeight, 120));

      let top = openUpward
        ? rect.top - menuHeight - 4
        : rect.bottom + 4;

      top = Math.max(
        viewportPadding,
        Math.min(top, window.innerHeight - menuHeight - viewportPadding)
      );

      setMenuStyle({
        position: 'fixed',
        top,
        left: rect.left,
        width: rect.width,
        maxHeight: menuHeight,
        zIndex: 10000,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, labelLines, filteredOptions.length]);

  const handleOptionSelect = (option) => {
    if (multiple) {
      const current = Array.isArray(value) ? value : [];
      const exists = current.find(v => (v?.[optionValue] ?? v) === option[optionValue]);
      const next = exists
        ? current.filter(v => (v?.[optionValue] ?? v) !== option[optionValue])
        : [...current, option];
      onChange(next);
    } else {
      onChange(option);
      setIsOpen(false);
    }
    setSearchTerm('');
  };

  const getLabelClassName = () => (
    labelLines > 1
      ? `searchable-select-label searchable-select-label-${labelLines}`
      : 'searchable-select-label searchable-select-label-1'
  );

  const getValueKey = (val) => {
    if (val === null || val === undefined || val === '') return '';
    if (typeof val === 'object') {
      return val[optionValue] ?? val.id ?? val.ID ?? '';
    }
    return val;
  };

  const getSelectedLabel = () => {
    if (!value || (multiple && (!Array.isArray(value) || value.length === 0))) return '';
    if (multiple) {
      return (value || []).map(v => v[optionLabel]).join(', ');
    }
    if (typeof value === 'object') {
      if (value[optionLabel]) return value[optionLabel];
      const matched = options.find(
        (option) => String(option[optionValue]) === String(getValueKey(value))
      );
      return matched?.[optionLabel] || '';
    }
    const matched = options.find(
      (option) => String(option[optionValue]) === String(value)
    );
    return matched?.[optionLabel] || '';
  };

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(multiple ? [] : null);
    setSearchTerm('');
    setIsOpen(false);
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) => {
        const primary = option[optionLabel] || '';
        const secondary = option.title || option.name || option.post_title || '';
        const haystack = `${primary} ${secondary}`.toLowerCase();
        return haystack.includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, optionLabel]);

  const handleTriggerClick = () => {
    if (disabled) return;

    if (isOpen) {
      setIsOpen(false);
      setSearchTerm('');
      return;
    }

    setIsOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const selectedLabel = getSelectedLabel();
  const hasValue = Boolean(getValueKey(value));
  const isMultiline = labelLines > 1;

  const dropdownMenu = isOpen && menuStyle ? (
    <div
      ref={dropdownRef}
      className={`searchable-select-dropdown ${isMultiline ? 'is-multiline' : ''}`}
      style={menuStyle}
    >
      <div className="searchable-select-search">
        <div className="relative">
          <MagnifyingGlassIcon className="searchable-select-search-icon" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={searchPlaceholder}
            className="searchable-select-search-input"
          />
        </div>
      </div>

      <div className="searchable-select-options">
        {filteredOptions.length === 0 ? (
          <div className="searchable-select-empty">No options found</div>
        ) : (
          <>
            <div className="searchable-select-options-meta">
              {filteredOptions.length} option(s) available
            </div>
            {filteredOptions.map((option, index) => {
              const selectedKey = getValueKey(value);
              const isSelected = multiple
                ? (Array.isArray(value) && value.some(v => String(v?.[optionValue] ?? v) === String(option[optionValue])))
                : String(option[optionValue]) === String(selectedKey);
              const optionText = option[optionLabel];

              return (
                <button
                  key={option[optionValue] || index}
                  type="button"
                  onClick={() => handleOptionSelect(option)}
                  className={`searchable-select-option ${isSelected ? 'is-selected' : ''} ${isMultiline ? 'is-multiline' : ''}`}
                >
                  <span
                    className={getLabelClassName()}
                    title={optionText || undefined}
                  >
                    {optionText}
                  </span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className={`relative min-w-0 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative min-w-0" ref={triggerRef}>
        <button
          type="button"
          onClick={handleTriggerClick}
          disabled={disabled}
          aria-expanded={isOpen}
          className={`
            relative w-full min-w-0 px-3 py-2 pr-10 text-left bg-white border border-gray-300 rounded-lg shadow-sm cursor-pointer
            focus:outline-none focus:ring-2 focus:ring-zinc-900/15 focus:border-zinc-900
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${isOpen ? 'ring-2 ring-zinc-900/15 border-zinc-900' : 'hover:border-gray-400'}
            ${isMultiline ? 'searchable-select-trigger-multiline' : ''}
            ${triggerClassName}
          `}
        >
          <span
            className={`${getLabelClassName()} ${!hasValue ? 'text-gray-500' : 'text-gray-900'}`}
            title={selectedLabel || undefined}
          >
            {selectedLabel || placeholder}
          </span>

          <span className={`searchable-select-trigger-icons ${isMultiline ? 'is-multiline' : ''}`}>
            {hasValue && (
              <div
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleClear(e);
                  }
                }}
                className="text-gray-400 hover:text-gray-600 mr-1 cursor-pointer"
                title="Clear selection"
              >
                <XMarkIcon className="h-4 w-4" />
              </div>
            )}
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </span>
        </button>

        {typeof document !== 'undefined' && dropdownMenu
          ? createPortal(dropdownMenu, document.body)
          : null}
      </div>
    </div>
  );
};

export default SearchableSelect;
